import React, { useEffect, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
  ResponsiveContainer,
} from 'recharts'
import { AppDispatch, RootState } from 'store'
import { MetricCard } from 'components/ui/MetricCard/MetricCard'
import { ChartCard } from '../components/ChartCard'
import { ChartTooltip } from '../components/ChartTooltip'
import { AnalyticsTable, RateCellRenderer, RedValueRenderer } from '../components/AnalyticsTable'
import { SubTabBar } from '../components/SubTabBar'
import { KpiRow, InfoBanner } from '../HrAnalytics.styled'
import {
  clearHrAnalytics,
  getScheduleByDepartment,
  getScheduleByRole,
  getScheduleDailyCoverage,
  getScheduleSummary,
} from 'features/hrAnalytics/hrAnalytics.slice'
import { tokens } from 'styling/tokens'

const KpiIcons: Record<string, React.ReactNode> = {
  calendar: <svg viewBox='0 0 24 24'><path d='M19 3h-1V1h-2v2H8V1H6v2H5c-1.11 0-1.99.9-1.99 2L3 19c0 1.1.89 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm0 16H5V8h14v11zM9 10H7v2h2v-2zm4 0h-2v2h2v-2zm4 0h-2v2h2v-2z' /></svg>,
  checkCircle: <svg viewBox='0 0 24 24'><path d='M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z' /></svg>,
  warning: <svg viewBox='0 0 24 24'><path d='M1 21h22L12 2 1 21zm12-3h-2v-2h2v2zm0-4h-2v-4h2v4z' /></svg>,
}

const GAP_COLUMNS = [
  { key: 'date', label: 'Date', sortable: true },
  { key: 'planned', label: 'Planned', sortable: true },
  { key: 'actual', label: 'Actual', sortable: true },
  {
    key: 'gap',
    label: 'Gap',
    sortable: true,
    renderCell: (val: number) => <RedValueRenderer value={val} />,
  },
  {
    key: 'coverage',
    label: 'Coverage',
    sortable: true,
    align: 'end' as const,
    renderCell: (val: number) => <RateCellRenderer value={val} />,
  },
]

const DEPT_COLUMNS = [
  { key: 'name', label: 'Name', sortable: true },
  { key: 'planned', label: 'Planned', sortable: true },
  { key: 'actual', label: 'Actual', sortable: true },
  {
    key: 'gap',
    label: 'Gap',
    sortable: true,
    renderCell: (val: number) => <RedValueRenderer value={val} />,
  },
  {
    key: 'coverage',
    label: 'Coverage',
    sortable: true,
    align: 'end' as const,
    renderCell: (val: number) => <RateCellRenderer value={val} />,
  },
]

const formatShortDate = (iso: string) => {
  const d = new Date(iso)
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
}

const toPercent = (ratio: number) => Math.round((ratio || 0) * 1000) / 10

interface IScheduleTabProps {
  startDate: Date
  endDate: Date
}

export const ScheduleTab = ({ startDate, endDate }: IScheduleTabProps) => {
  const dispatch = useDispatch<AppDispatch>()
  const [tableTab, setTableTab] = useState('department')

  const {
    scheduleSummary,
    scheduleByDepartment,
    scheduleByRole,
    scheduleDailyCoverage,
  } = useSelector((state: RootState) => state.hrAnalytics)

  useEffect(() => {
    if (!startDate || !endDate) return
    const params = { startDate, endDate }
    dispatch(getScheduleSummary(params))
    dispatch(getScheduleByDepartment(params))
    dispatch(getScheduleByRole(params))
    dispatch(getScheduleDailyCoverage(params))
    return () => {
      dispatch(clearHrAnalytics())
    }
  }, [startDate, endDate, dispatch])

  const kpis = useMemo(
    () => [
      {
        value: (scheduleSummary?.planned ?? 0).toLocaleString(),
        label: 'PLANNED',
        sublabel: 'Shift assignments in range',
        accentColor: tokens.status.info.fg,
        iconKey: 'calendar',
      },
      {
        value: (scheduleSummary?.actual ?? 0).toLocaleString(),
        label: 'ACTUAL',
        sublabel: 'Finalized attendance records',
        accentColor: tokens.status.success.fg,
        iconKey: 'checkCircle',
      },
      {
        value: `${toPercent(scheduleSummary?.coverageRatio ?? 0)}%`,
        label: 'COVERAGE',
        accentColor: tokens.accent.base,
        iconKey: 'warning',
      },
    ],
    [scheduleSummary],
  )

  const plannedVsActual = useMemo(
    () =>
      scheduleDailyCoverage.map((d) => ({
        date: formatShortDate(d.date),
        planned: d.planned,
        actual: d.actual,
      })),
    [scheduleDailyCoverage],
  )

  const coverageGaps = useMemo(
    () =>
      [...scheduleDailyCoverage]
        .sort((a, b) => b.gap - a.gap)
        .slice(0, 10)
        .map((d) => ({
          date: formatShortDate(d.date),
          planned: d.planned,
          actual: d.actual,
          gap: d.gap,
          coverage: toPercent(d.coverageRatio),
        })),
    [scheduleDailyCoverage],
  )

  const deptRows = useMemo(
    () =>
      scheduleByDepartment.map((d) => ({
        name: d.departmentName,
        planned: d.planned,
        actual: d.actual,
        gap: d.gap,
        coverage: toPercent(d.coverageRatio),
      })),
    [scheduleByDepartment],
  )

  const roleRows = useMemo(
    () =>
      scheduleByRole.map((r) => ({
        name: r.roleName,
        planned: r.planned,
        actual: r.actual,
        gap: r.gap,
        coverage: toPercent(r.coverageRatio),
      })),
    [scheduleByRole],
  )

  const tableData = tableTab === 'department' ? deptRows : roleRows

  return (
    <>
      <KpiRow>
        {kpis.map((kpi, idx) => (
          <MetricCard
            key={idx}
            layout='inline'
            icon={KpiIcons[kpi.iconKey || '']}
            value={kpi.value}
            label={kpi.label}
            sublabel={kpi.sublabel}
            accentColor={kpi.accentColor}
          />
        ))}
      </KpiRow>

      <InfoBanner>
        Schedule analytics compares planned obligations to finalized outcomes. It does not alter the schedule or enforce compliance.
      </InfoBanner>

      <ChartCard
        title='Planned vs Actual'
      // periods={['Day', 'Week', 'Month']}
      // activePeriod={period}
      // onPeriodChange={setPeriod}
      // legend={[
      //   { label: 'Actual', color: '#00B04C' },
      //   { label: 'Planned', color: '#FF4F46' },
      // ]}
      >
        <ResponsiveContainer width='100%' height={300}>
          <BarChart data={plannedVsActual} barGap={2}>
            <CartesianGrid strokeDasharray='3 3' stroke={tokens.border.hairline} />
            <XAxis dataKey='date' tick={{ fontSize: 11, fill: tokens.text.muted }} tickLine={false} />
            <YAxis tick={{ fontSize: 11, fill: tokens.text.muted }} tickLine={false} />
            <Tooltip cursor={{ fill: tokens.surface.inset }} content={<ChartTooltip />} />
            <Legend
              iconType='circle'
              iconSize={9}
              wrapperStyle={{ fontSize: 12, color: tokens.text.secondary, paddingTop: 8 }}
            />
            <Bar dataKey='actual' name='Actual' fill={tokens.status.success.fg} radius={[4, 4, 0, 0]} barSize={14} />
            <Bar dataKey='planned' name='Planned' fill={tokens.status.danger.fg} radius={[4, 4, 0, 0]} barSize={14} />
          </BarChart>
        </ResponsiveContainer>
      </ChartCard>

      <ChartCard title='Largest Coverage Gaps'>
        <AnalyticsTable columns={GAP_COLUMNS} data={coverageGaps} />
      </ChartCard>

      <SubTabBar
        tabs={[
          { key: 'department', label: 'By Department' },
          { key: 'role', label: 'By Role' },
        ]}
        activeTab={tableTab}
        onTabChange={setTableTab}
      />

      <AnalyticsTable columns={DEPT_COLUMNS} data={tableData} />
    </>
  )
}
